/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { type ContextValue, DEFAULT_SLOT, Provider, ToggleButton as ToggleButtonCore, type ToggleButtonProps as ToggleButtonCoreProps, useContextProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { IconContext } from '../icon/icon.component';
import { Text, TextContext, type TextProps } from '../text/text.component';

interface ToggleButtonProps extends Omit<ToggleButtonCoreProps, 'style' | 'className'> {
  /** The variant of the toggle button. (Default: filled) */
  variant?: keyof typeof variantVariants;
  /** The size variant of the toggle button. (Default: 28) */
  size?: keyof typeof sizeVariants;
  /** Whether the toggle button is rounded. (Default: false) */
  isRounded?: boolean;
  style?: stylex.StyleXStyles;
}

const ToggleButtonContext = createContext<ContextValue<ToggleButtonProps, HTMLButtonElement>>({});

const ToggleButton = forwardRef((props: ToggleButtonProps, ref: ForwardedRef<HTMLButtonElement>) => {
  [props, ref] = useContextProps(props, ref, ToggleButtonContext);

  const { style, size = 28, isRounded = false, variant = 'filled', ...coreProps } = props;
  let { children } = coreProps;

  return (
    <ToggleButtonCore
      ref={ref}
      {...stylex.props(styles.base, sizeVariants[size], isRounded && styles.isRounded, variantVariants[variant], style)}
      {...coreProps}>
      {(renderProps) => {
        children = typeof children === 'function' ? children(renderProps) : children;

        const isTextOnly = Children.toArray(children).every(c => !isValidElement(c));

        const contentColor = (
          renderProps.isDisabled && 'disabled' ||
          (
            variant === 'outlined' &&
            (
              renderProps.isSelected && 'interactive' ||
              renderProps.isHovered && 'tertiary' ||
              'quaternary'
            )
          ) ||
          'interactive'
        );

        return (
          <Provider
            values={[
              [TextContext, {
                size: TEXT_SIZES[size],
                color: contentColor,
              }],
              [IconContext, {
                slots: {
                  [DEFAULT_SLOT]: {
                    style: iconStyles.base,
                    color: contentColor,
                  },
                  [TOGGLE_BUTTON_SLOTS.LEFT_ICON]: {
                    size: LEFT_ICON_SIZE[size],
                    color: contentColor,
                    style: iconStyles.base,
                  },
                  [TOGGLE_BUTTON_SLOTS.RIGHT_ICON]: {
                    size: RIGHT_ICON_SIZE[size],
                    color: contentColor,
                    style: iconStyles.base,
                  }
                }
              }],
            ]}>
            {typeof children === 'string' || isTextOnly ? <Text>{children}</Text> : children}
          </Provider>
        );
      }}
    </ToggleButtonCore>
  );
});

const TEXT_SIZES: Record<keyof typeof sizeVariants, TextProps['size']> = {
  36: 14,
  32: 14,
  28: 12,
  24: 12,
  20: 11,
};


const LEFT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  20: 12,
  24: 12,
  28: 12,
  32: 16,
  36: 16,

};

const RIGHT_ICON_SIZE: Record<keyof typeof sizeVariants, number> = {
  20: 10,
  24: 12,
  28: 12,
  32: 12,
  36: 12,
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: 1,
    boxSizing: 'border-box',
    cursor: {
      default: 'pointer',
      ':is([data-disabled])': 'not-allowed',
      ':is([data-pending])': 'auto',
    },
    display: 'inline-flex',
    gap: 5,
    isolation: 'isolate',
    position: 'relative',
    textDecoration: 'none',
    transitionDuration: '0.2s',
    transitionProperty: 'border-color, background-color',
    transitionTimingFunction: 'ease-in-out',
    userSelect: 'none',
  },
  isRounded: {
    borderRadius: 999,
  }
});

const variantVariants = stylex.create({
  filled: {
    backgroundColor: {
      default: $.buttonSurfaceTertiary,
      ':is([data-hovered])': $.buttonSurfaceTertiaryHover,
      ':is([data-selected])': $.buttonSurfaceTertiary,
      ':is([data-disabled])': $.buttonSurfaceDisabled,
    },
    borderColor: {
      default: 'transparent',
      ':is([data-selected])': $.borderInteractive,
      ':is([data-disabled])': 'transparent',
    },
  },
  outlined: {
    backgroundColor: 'transparent',
    borderColor: {
      default: $.border10,
      ':is([data-hovered])': $.border24,
      ':is([data-selected])': `${$.borderInteractive} !important`,
      ':is([data-disabled])': `${$.border08} !important`,
    },
  }
});

const sizeVariants = stylex.create({
  36: {
    borderRadius: 8,
    height: 36,
    paddingBlock: 9,
    paddingInline: 12,
  },
  32: {
    borderRadius: 8,
    height: 32,
    paddingBlock: 7,
    paddingInline: 12,
  },
  28: {
    borderRadius: 6,
    height: 28,
    paddingBlock: 6,
    paddingInline: 10,
  },
  24: {
    borderRadius: 6,
    height: 24,
    paddingBlock: 4,
    paddingInline: 8,
  },
  20: {
    borderRadius: 4,
    height: 20,
    paddingBlock: 0,
    paddingInline: 8,
  },
});

const iconStyles = stylex.create({
  base: {
    transitionDuration: '0.2s',
    transitionProperty: 'stroke, fill',
    transitionTimingFunction: 'easy-in-out',
  },
});

const TOGGLE_BUTTON_SLOTS = {
  LEFT_ICON: 'leftIcon',
  RIGHT_ICON: 'rightIcon',
};

export { TOGGLE_BUTTON_SLOTS, ToggleButton, ToggleButtonContext, type ToggleButtonProps };
